<template>
    <div>
      <Metadata :title="badge.name" :description="badge.description" :image="badge.image"></Metadata>
      <Nav :links="links" :cta="cta"></Nav>
      <div>
        <div class="grid grid-cols-5 gap-4">
          <img v-if="badge.image_png" :src="badge.image_png" :alt="badge.name" class="w-36 float-right ml-5" />
          <div class="col-span-4">
            <p>Micro-Certification</p>
            <h1
              class="text-6xl font-extrabold leading-none tracking-tight text-gray-900 dark:text-white">
              {{ badge.name }}
            </h1>
            
  
            <p
              class="text-2xl mt-5 mr-3 text-gray-700 dark:text-gray-400"
            >
              {{ badge.description }}
            </p>
          </div>
    
          
        </div>
  
        <Section id="criteria" heading="Earning Criteria" v-if="badge.criteriaNarrative">    
            <div class="mb-5">
              <Markdown :text="badge.criteriaNarrative" />
            </div>
          </Section>

        <UCard class="bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-700 dark:border-gray-400 mt-20">    
        <Section id="get-certified" heading="Get Certified" classes="mt-3" >    

          <div class="mb-5">
            To earn this certification, you must demonstrate mastery of the skills listed in the "Earning Criteria" section above. 
            Look over the "Earning Criteria" and make sure you are ready to demonstrate your skills in a live evaluation with an expert. 
            Once you are ready, you can request an evaluation by clicking the button below.
          </div>
          <NuxtLink :to="eval_url"
                target="_blank"
                rel="nofollow"
                class="inline-flex items-center px-3 py-2 font-medium text-center text-white bg-blue-700 rounded-lg hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                :alt="`Request evaluation for ${badge.name}`">
        Request Evaluation
        <svg class="rtl:rotate-180 w-3.5 h-3.5 ms-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 5h12m0 0L9 1m4 4L9 9"/>
                </svg>
      </NuxtLink>
        </Section>
        </UCard>      
  
        <Section id="resources" heading="Training Resources" v-if="resources.length>0">
          <div
            class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <ResourceCard
              v-for="resource in resources"
              class="m-4"
              :key="resource._id"
              :resource="resource" />
          </div>
        </Section>
        
        <Section id="related-certs" heading="Related Certifications" v-if="related_badges.length>0">
          <div class="grid grid-cols-1 gap-4">
            <BadgeListing
              :badge="related_badge"
              class="my-5"
              v-for="related_badge in related_badges"
              :key="related_badge"
            />
          </div>
        </Section>
        
        <Section id="growth" heading="Accelerate Your Growth">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-10">
            <PromoCard v-bind="promo" v-for="promo in promos" :key="promo" />
          </div>  
        </Section>
      </div>
    </div>
  </template>
  
  <script setup>
  import _ from "lodash";
  import { useSkillStore } from "../store";
  
  const store = useSkillStore();
  
  const route = useRoute();
  const {badge_id} = route.params;
  
  const cta = {label: "Browse Skill Library", url: "/"};
  
  const badge = store.getBadge(badge_id);
  //const skill = store.getSkill(badge.skill_id);
  const resources = store.getResourcesForBadge(badge_id);
  const related_badges = store.getBadgesForSkill(badge.skill_id).filter(x=> x.entityId!==badge_id); 
  const eval_url = badge.entityId ? `https://scheduling.growstrong.io/?badge=${badge.entityId}` : `https://scheduling.growstrong.io/?badge_id=${badge._id}`;
  //const tags = badge.tags?.split(",");
  
  const promos = [
    {
      image_url:
        "https://res.cloudinary.com/sommardahl-ventures/image/upload/v1718997815/devamplifier/site_assets/struggling_a6xdyx.jpg",
      link_url: "https://devamplifier.io/skillgaps",
      headline: "Discover Your Skill Gaps",
      caption:
        "Gaps in your skills can slow you down or even hold you back from reaching your goals. Use our FREE tool to help you discover and fill your skill gaps starting today!",
      call_to_action: "Take the Assessment",
    },
    {
      image_url:
        "https://res.cloudinary.com/sommardahl-ventures/image/upload/v1718997767/devamplifier/site_assets/mentoring_egyprk.jpg",
      link_url: "https://devamplifier.io/book-a-mentor",
      headline: "Get Help From a Mentor",
      caption:
        "Got skill gaps? Dev Amplifier is here to help you fill them! Book a live, 1-on-1 session with one of our growth-oriented mentors and get the boost your career needed!",
      call_to_action: "Schedule a Mentoring Session",
    },
    {
      image_url:
        "https://res.cloudinary.com/sommardahl-ventures/image/upload/v1718997797/devamplifier/site_assets/mentoring_a3aeby.jpg",
      link_url: "https://devamplifier.io/gameplan",
      headline: "Meet with a Coach",
      caption:
        'Dev Amplifier believes the best way to get control of your career is to meet with an experienced coach to help develop a "game plan."',
      call_to_action: "Get Your Game Plan",
    },
  ];
  
  const links = [
      {
        label: badge.name,
        icon: "i-heroicons-building-library",
        to: { hash: `#` }
      },
      {
        label: "Earn",
        icon: "i-heroicons-building-library",
        to: { hash: `#get-certified` }
      }
   ];
   
   if(resources.length>0){
     links.push({
        label: "Train",
        icon: "i-heroicons-adjustments-horizontal",
        to: { hash: `#resources` },
      });
   }
  
   if(related_badges.length>0){
     links.push({
        label: "Extend",
        icon: "i-heroicons-adjustments-horizontal",
        to: { hash: `#related-certs` },
      });
   }
  </script>
  
  <style scoped>
  /* CSS specific to this page */
  </style>
  